import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { Logo } from "@/components/Logo";
import { CATEGORIES_UI } from "@/home/content";

const NAV = [
  { label: "Home", to: "/" },
  { label: "About", to: "/about" },
  { label: "Alcoholic Portfolio", to: "/portfolio", dropdown: true },
  { label: "Distribution", to: "/distribution" },
  { label: "Infrastructure", to: "/infrastructure" },
  { label: "Partner With Us", to: "/partner" },
  { label: "Contact", to: "/contact" },
];

export const SiteHeader = ({ variant = "light" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobilePortfolio, setMobilePortfolio] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobilePortfolio(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const dark = variant === "dark";
  // ONE consistent solid treatment on every page: ivory bar + charcoal text.
  // The header is transparent only over the dark homepage hero before scroll,
  // where all text switches to ivory for contrast.
  const transparent = dark && !scrolled;
  const base = transparent
    ? "bg-transparent border-b border-transparent"
    : "bg-[#F7F1E7]/95 backdrop-blur-md border-b border-[#111111]/10 shadow-[0_1px_14px_rgba(17,17,17,0.07)]";
  const text = transparent ? "text-[#F7F1E7]" : "text-[#1c1713]";
  const linkCls = `text-[11px] uppercase tracking-[0.16em] font-medium transition-colors duration-300 ${
    transparent ? "text-[#F7F1E7]/80 hover:text-[#C7A45D]" : "text-[#1c1713]/70 hover:text-[#4B0F1E]"
  }`;

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-500 ${base} ${text}`}
      >
        <div className="flex items-center justify-between px-5 sm:px-8 xl:px-12 h-[72px]">
          <Link to="/" data-testid="header-logo-link" aria-label="Hema Connoisseur Collections — Home">
            <Logo width={96} onDark={transparent} />
          </Link>

          <nav className="hidden lg:flex items-center gap-6 xl:gap-8" aria-label="Primary">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.label} className="relative group">
                  <Link
                    to={item.to}
                    data-testid="nav-portfolio"
                    className={`${linkCls} inline-flex items-center gap-1.5`}
                  >
                    {item.label}
                    <ChevronDown size={12} strokeWidth={1.5} />
                  </Link>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 invisible opacity-0 translate-y-1 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 transition-all duration-300">
                    <div
                      data-testid="portfolio-dropdown"
                      className="w-60 bg-[#F7F1E7] text-[#1c1713] shadow-2xl border border-[#111111]/10 py-3"
                    >
                      {CATEGORIES_UI.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/portfolio?category=${c.slug}`}
                          data-testid={`dropdown-${c.slug}`}
                          className="block px-6 py-2 text-[11px] uppercase tracking-[0.16em] text-[#1c1713]/70 hover:text-[#4B0F1E] hover:bg-[#111111]/[0.04] transition-colors"
                        >
                          {c.name}
                        </Link>
                      ))}
                      <div className="mx-6 my-2 h-px bg-[#C7A45D]/40" />
                      <Link
                        to="/portfolio"
                        className="block px-6 py-2 text-[11px] uppercase tracking-[0.16em] text-[#4B0F1E] font-semibold"
                      >
                        View Full Portfolio
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <Link key={item.label} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`} className={linkCls}>
                  {item.label}
                </Link>
              )
            )}
          </nav>

          <div className="flex items-center gap-4">
            <Link
              to="/contact"
              data-testid="header-trade-enquiry"
              className={`hidden lg:inline-block text-[11px] uppercase tracking-[0.18em] font-semibold px-5 py-2.5 border transition-all duration-300 ${
                transparent
                  ? "border-[#C7A45D]/60 text-[#C7A45D] hover:bg-[#C7A45D] hover:text-[#111111]"
                  : "border-[#1c1713] text-[#1c1713] hover:bg-[#1c1713] hover:text-[#F7F1E7]"
              }`}
            >
              Trade Enquiry
            </Link>
            <button
              data-testid="mobile-menu-button"
              className="lg:hidden p-2"
              aria-label="Open menu"
              onClick={() => setOpen(true)}
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile full-screen menu */}
      {open && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-50 bg-[#111111] text-[#F7F1E7] flex flex-col overflow-y-auto">
          <div className="flex items-center justify-between px-5 h-[72px] border-b border-white/10">
            <Logo width={92} onDark />
            <button data-testid="mobile-menu-close" aria-label="Close menu" onClick={() => setOpen(false)} className="p-2">
              <X size={24} strokeWidth={1.5} />
            </button>
          </div>
          <nav className="flex-1 px-7 py-10 space-y-1" aria-label="Mobile">
            {NAV.map((item) =>
              item.dropdown ? (
                <div key={item.label}>
                  <button
                    data-testid="mobile-nav-portfolio"
                    className="w-full flex items-center justify-between py-3.5 hcc-serif text-2xl text-[#F7F1E7]"
                    onClick={() => setMobilePortfolio((v) => !v)}
                  >
                    {item.label}
                    <ChevronDown size={18} className={`transition-transform duration-300 ${mobilePortfolio ? "rotate-180" : ""}`} />
                  </button>
                  {mobilePortfolio && (
                    <div className="pl-5 pb-3 space-y-1 border-l border-[#C7A45D]/40">
                      {CATEGORIES_UI.map((c) => (
                        <Link
                          key={c.slug}
                          to={`/portfolio?category=${c.slug}`}
                          className="block py-2 text-sm uppercase tracking-[0.14em] text-[#F7F1E7]/70"
                        >
                          {c.name}
                        </Link>
                      ))}
                      <Link to="/portfolio" className="block py-2 text-sm uppercase tracking-[0.14em] text-[#C7A45D]">
                        View Full Portfolio
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block py-3.5 hcc-serif text-2xl text-[#F7F1E7]"
                >
                  {item.label}
                </Link>
              )
            )}
            <div className="pt-8">
              <Link
                to="/contact"
                data-testid="mobile-trade-enquiry"
                className="inline-block text-[11px] uppercase tracking-[0.18em] font-semibold px-6 py-3 border border-[#C7A45D]/70 text-[#C7A45D]"
              >
                Trade Enquiry
              </Link>
            </div>
            <div className="pt-10 text-[10px] tracking-[0.3em] uppercase text-[#F7F1E7]/35">
              Imports · Distribution · Market Access
            </div>
          </nav>
        </div>
      )}
    </>
  );
};
